import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton, Skeleton, EmptyState } from "@/components/ios";
import {
  ChevronLeft, FileArchive, Fingerprint, ShieldCheck, Printer, Download, Plus,
  Clock, Mail, Image as ImageIcon, GitCommitVertical, Scale,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const SEV_TONE = { none: "neutral", minor: "warning", moderate: "warning", major: "danger" };

export default function EvidencePack() {
  const { id } = useParams();
  const nav = useNavigate();
  const [packs, setPacks] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    const { data } = await api.get(`/evidence-packs?report_id=${id}`);
    setPacks(data);
    if (data.length && !active) openPack(data[0].id);
  }, [id]); // eslint-disable-line

  useEffect(() => { loadList(); }, [loadList]);

  const openPack = async (pid) => {
    const { data } = await api.get(`/evidence-packs/${pid}`);
    setActive(data);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${id}/evidence-pack`);
      toast.success(`Evidence pack v${data.version} generated`, { icon: <ShieldCheck size={16} /> });
      const { data: list } = await api.get(`/evidence-packs?report_id=${id}`);
      setPacks(list);
      openPack(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const download = () => {
    const blob = new Blob([JSON.stringify(active.artefact, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `evidence-pack-v${active.version}.json`;
    a.click();
  };

  if (!packs) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  const a = active?.artefact;

  return (
    <div className="pb-16">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(`/reports/${id}/review`)} className="flex items-center gap-1 text-sm text-secondary"><ChevronLeft size={16} /> Report</button>
        <div className="flex gap-2">
          {active && <AButton variant="text" onClick={() => window.print()}><Printer size={14} className="inline mr-1" /> Print</AButton>}
          {active && <AButton variant="text" onClick={download}><Download size={14} className="inline mr-1" /> JSON</AButton>}
          <AButton testid="generate-pack" onClick={generate} disabled={busy}><Plus size={15} className="inline mr-1" /> {busy ? "Generating…" : "New pack"}</AButton>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-1">
        <FileArchive className="accent-text" size={26} />
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Tribunal Evidence Pack</h1>
      </div>
      <p className="text-sm text-secondary mb-5">Immutable, independently verifiable bundle for NCAT / VCAT / QCAT.</p>

      {packs.length > 1 && (
        <div className="flex gap-2 mb-5 flex-wrap print:hidden">
          {packs.map((p) => (
            <button key={p.id} onClick={() => openPack(p.id)} data-testid={`pack-v${p.version}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border" style={{
                background: active?.id === p.id ? "var(--accent)" : "var(--surface)",
                color: active?.id === p.id ? "#fff" : "var(--text)", borderColor: "var(--border)" }}>
              Version {p.version}
            </button>
          ))}
        </div>
      )}

      {!active ? (
        <Surface><EmptyState icon={FileArchive} title="No evidence pack yet"
          subtitle="Generate a one-click tribunal bundle: both report versions, the finding→media chain with hashes, notice proofs and an audit chronology."
          action={<AButton onClick={generate} disabled={busy}>{busy ? "Generating…" : "Generate pack"}</AButton>} /></Surface>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* integrity header */}
          <Surface className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-lg" style={{ color: "var(--text)" }}>{a.property?.address}, {a.property?.suburb} {a.property?.state}</div>
                <div className="text-sm text-secondary capitalize">{a.report_type} report · schema v{a.schema_version} · pack v{active.version}</div>
              </div>
              <Pill tone="accent">Generated {new Date(active.generated_at).toLocaleString("en-AU")}</Pill>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs p-2.5 rounded-lg" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
              <Fingerprint size={13} /> Content hash (SHA-256): <span className="font-mono break-all" style={{ color: "var(--text)" }}>{active.content_hash}</span>
            </div>
          </Surface>

          {/* parties */}
          <Section icon={Scale} title="Parties">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><div className="text-xs text-secondary uppercase">Tenant(s)</div>{a.parties.tenants.map((t, i) => <div key={i} className="text-sm" style={{ color: "var(--text)" }}>{t.name} · {t.email}</div>)}</div>
              <div><div className="text-xs text-secondary uppercase">Owner(s)</div>{a.parties.owners.map((o, i) => <div key={i} className="text-sm" style={{ color: "var(--text)" }}>{o.name} · {o.email}</div>)}</div>
            </div>
          </Section>

          {/* report versions */}
          <Section icon={GitCommitVertical} title="Report versions & signatures">
            <div className="space-y-1.5">
              {a.report_versions.version_chain.map((v, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full accent-bg" />
                  <span className="capitalize" style={{ color: "var(--text)" }}>{v.status.replace(/_/g, " ")}</span>
                  <span className="text-secondary text-xs">· {new Date(v.at).toLocaleString("en-AU")} · {v.hash?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              {a.signatures.length === 0 ? <div className="text-sm text-secondary">No signatures recorded.</div> :
                a.signatures.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm" style={{ color: "var(--success)" }}>
                    <ShieldCheck size={14} /> {s.signer} ({s.signer_role}) signed version {s.version_hash?.slice(0, 10)} · {new Date(s.at).toLocaleString("en-AU")} · IP {s.ip || "n/a"}
                  </div>
                ))}
            </div>
            <div className="mt-3 text-xs text-secondary">Both the AI-original draft and the agent-approved final are preserved in this pack for comparison ({a.review_actions.length} human review actions logged).</div>
          </Section>

          {/* evidence chains */}
          <Section icon={ImageIcon} title={`Finding → media evidence chain (${a.evidence_chains.length})`}>
            {a.evidence_chains.length === 0 ? <div className="text-sm text-secondary">No adverse findings recorded.</div> :
              <div className="space-y-4">
                {a.evidence_chains.map((c, i) => (
                  <div key={i} className="pb-3 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium" style={{ color: "var(--text)" }}>{c.room} — {c.item}</span>
                      <Pill tone={SEV_TONE[c.severity]}>{c.severity}</Pill>
                    </div>
                    <p className="text-sm text-secondary mt-0.5">{c.comment}</p>
                    <div className="flex gap-3 mt-2 flex-wrap">
                      {c.media.map((m) => (
                        <div key={m.media_id} className="w-40">
                          <img src={mediaUrl(m.signed_url)} className="w-40 h-28 object-cover rounded-lg" alt="evidence" />
                          <div className="text-[10px] text-secondary mt-1 leading-tight">
                            <div className="flex items-center gap-1"><Fingerprint size={9} /> {m.sha256?.slice(0, 20)}…</div>
                            <div>{new Date(m.captured_at).toLocaleString("en-AU")}</div>
                            <div>{m.device_meta?.device}{m.redacted ? " · PII redacted" : ""}</div>
                          </div>
                        </div>
                      ))}
                      {c.media.length === 0 && <span className="text-xs text-secondary">No media attached to this finding.</span>}
                    </div>
                  </div>
                ))}
              </div>}
          </Section>

          {/* notice proofs */}
          <Section icon={Mail} title="Notice delivery proofs">
            {a.notice_delivery_proofs.notices.length === 0 && a.notice_delivery_proofs.notifications.length === 0 ?
              <div className="text-sm text-secondary">No notices on record for this property.</div> :
              <div className="space-y-1.5 text-sm">
                {a.notice_delivery_proofs.notices.map((n) => (
                  <div key={n.id} className="flex items-center justify-between gap-2">
                    <span style={{ color: "var(--text)" }} className="capitalize">{n.report_type} entry notice · {new Date(n.entry_date).toLocaleDateString("en-AU")} · via {n.delivery_method}</span>
                    <Pill tone={n.compliant ? "success" : "danger"}>{n.compliant ? `${n.notice_days}d notice` : "short notice"}</Pill>
                  </div>
                ))}
                {a.notice_delivery_proofs.notifications.map((n) => (
                  <div key={n.id} className="text-secondary text-xs">Report delivered to {n.to} via {n.channel} · {n.status} · {new Date(n.at).toLocaleString("en-AU")}</div>
                ))}
              </div>}
          </Section>

          {/* chronology */}
          <Section icon={Clock} title={`Chronology (${a.chronology.length} audit events)`}>
            <div className="space-y-1.5">
              {a.chronology.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-secondary w-40 shrink-0">{new Date(e.at).toLocaleString("en-AU")}</span>
                  <Pill tone="neutral">{e.action.replace(/_/g, " ")}</Pill>
                  <span style={{ color: "var(--text)" }}>{e.actor}</span>
                  <span className="text-secondary text-xs">· {e.entity}{e.ip ? ` · ${e.ip}` : ""}</span>
                </div>
              ))}
            </div>
          </Section>

          <p className="text-xs text-secondary text-center">This artefact is immutable — regenerating creates a new version. File hashes can be independently verified against the media files.</p>
        </motion.div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <Surface className="p-5">
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-secondary mb-3">
        <Icon size={15} className="accent-text" /> {title}
      </div>
      {children}
    </Surface>
  );
}
